import React, { useState } from 'react';
import { Box, Typography, Avatar, Chip, Grid, Divider, TextField, Button, Snackbar, Alert } from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import LockIcon from '@mui/icons-material/Lock';
import PersonIcon from '@mui/icons-material/Person';
import EmailIcon from '@mui/icons-material/Email';
import PhoneIcon from '@mui/icons-material/Phone';
import HomeIcon from '@mui/icons-material/Home';
import LocationCityIcon from '@mui/icons-material/LocationCity';
import MapIcon from '@mui/icons-material/Map';
import PublicIcon from '@mui/icons-material/Public';
import BusinessIcon from '@mui/icons-material/Business';
import WhatsAppIcon from '@mui/icons-material/WhatsApp';
import PhoneIphoneIcon from '@mui/icons-material/PhoneIphone';
import GroupsIcon from '@mui/icons-material/Groups';
import { updateAccountDetails } from '../api/whatsapp';
import { changePassword } from '../api/auth';
import { BLOCKED_STATUS_LABEL } from '../utils/blockedStatus';

const getInitials = (name) => {
  if (!name) return '?';
  const parts = name.trim().split(/\s+/);
  if (parts.length >= 2) return (parts[0][0] + parts[1][0]).toUpperCase();
  return name.slice(0, 2).toUpperCase();
};

const AccountDetails = ({ account, onUpdated }) => {
  const u = account?.userId || {};
  const status = account?.onboardingStatus || 'N/A';
  const active = status === 'active' || status === 'otp_verified';
  const blocked = account?.apiStatus === 'blocked';

  const [editing, setEditing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({});
  const [snack, setSnack] = useState({ open: false, message: '', severity: 'success' });
  const [pwOpen, setPwOpen] = useState(false);
  const [pwForm, setPwForm] = useState({ oldPassword: '', newPassword: '' });
  const [pwSaving, setPwSaving] = useState(false);

  const editableFields = [
    { key: 'fullName', label: 'Full Name', icon: <PersonIcon />, value: u.fullName || '' },
    { key: 'email', label: 'Email', icon: <EmailIcon />, value: u.email || '' },
    { key: 'contactMobile', label: 'Contact Mobile', icon: <PhoneIcon />, value: account?.contactMobile || '' },
    { key: 'businessPhone', label: 'Business Phone', icon: <PhoneIphoneIcon />, value: account?.businessPhone || '' },
    { key: 'address', label: 'Address', icon: <HomeIcon />, value: account?.address || '' },
    { key: 'city', label: 'City', icon: <LocationCityIcon />, value: account?.city || '' },
    { key: 'state', label: 'State', icon: <MapIcon />, value: account?.state || '' },
    { key: 'country', label: 'Country', icon: <PublicIcon />, value: account?.country || '' },
  ];

  const lockedFields = [
    { label: 'Company Name', icon: <BusinessIcon />, value: account?.companyName || '' },
    { label: 'WhatsApp Business Number', icon: <WhatsAppIcon />, value: account?.whatsappBusinessNumber || '' },
    { label: 'Facebook BM ID', icon: <GroupsIcon />, value: account?.facebookBusinessManagerId || '' },
  ];

  const startEdit = () => {
    const init = {};
    editableFields.forEach((f) => { init[f.key] = f.value || ''; });
    setForm(init);
    setEditing(true);
  };

  const cancelEdit = () => {
    setEditing(false);
    setForm({});
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await updateAccountDetails(form);
      setSnack({ open: true, message: 'Account details updated', severity: 'success' });
      setEditing(false);
      if (onUpdated) onUpdated();
    } catch (err) {
      const detail = err?.response?.data?.message || err.message || 'Failed to update';
      setSnack({ open: true, message: detail, severity: 'error' });
    }
    setSaving(false);
  };

  const handleChangePassword = async () => {
    if (!pwForm.oldPassword || !pwForm.newPassword) {
      setSnack({ open: true, message: 'Enter both old and new password', severity: 'error' });
      return;
    }
    setPwSaving(true);
    try {
      await changePassword(pwForm.oldPassword, pwForm.newPassword);
      setSnack({ open: true, message: 'Password changed successfully', severity: 'success' });
      setPwForm({ oldPassword: '', newPassword: '' });
      setPwOpen(false);
    } catch (err) {
      const detail = err?.response?.data?.message || err.message || 'Failed to change password';
      setSnack({ open: true, message: detail, severity: 'error' });
    }
    setPwSaving(false);
  };

  const renderValue = (f) => (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, p: 1.25, borderRadius: 2, bgcolor: '#f8fafc', border: '1px solid #eef2f7', minHeight: 52 }}>
      <Box sx={{
        width: 32, height: 32, borderRadius: 2, flexShrink: 0,
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        bgcolor: '#dcfce7', color: '#075e54', fontSize: 17,
      }}>
        {f.icon}
      </Box>
      <Box sx={{ minWidth: 0, flex: 1 }}>
        <Typography fontSize={11} color="#94a3b8" fontWeight={600}>{f.label}</Typography>
        <Typography fontSize={13.5} color="#1e293b" fontWeight={600} sx={{ wordBreak: 'break-word' }}>
          {f.value || '—'}
        </Typography>
      </Box>
    </Box>
  );

  const renderInput = (f) => (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, p: 1.25, borderRadius: 2, bgcolor: '#f8fafc', border: '1px solid #eef2f7', minHeight: 52 }}>
      <Box sx={{
        width: 32, height: 32, borderRadius: 2, flexShrink: 0,
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        bgcolor: '#dcfce7', color: '#075e54', fontSize: 17,
      }}>
        {f.icon}
      </Box>
      <TextField
        size="small"
        label={f.label}
        value={form[f.key] || ''}
        onChange={(e) => setForm({ ...form, [f.key]: e.target.value })}
        fullWidth
        sx={{ "& .MuiOutlinedInput-root": { bgcolor: '#fff', borderRadius: 1.5 } }}
      />
    </Box>
  );

  return (
    <Box>
      {/* Profile header */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3 }}>
        <Avatar sx={{ width: 56, height: 56, bgcolor: '#075e54', fontSize: 20, fontWeight: 800 }}>
          {getInitials(u.fullName)}
        </Avatar>
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography fontWeight={700} fontSize={17} color="#1e293b" sx={{ wordBreak: 'break-word' }}>
            {u.fullName || '—'}
          </Typography>
          <Typography variant="body2" color="#64748b" fontSize={13} sx={{ wordBreak: 'break-word' }}>
            {u.email || '—'}
          </Typography>
        </Box>
        <Chip label={blocked ? BLOCKED_STATUS_LABEL : active ? 'Active' : status.replace(/_/g, ' ')} size="small"
          sx={{
            fontWeight: 700, fontSize: 11, textTransform: 'capitalize',
            bgcolor: blocked ? '#fee2e2' : active ? '#dcfce7' : '#fef3c7', color: blocked ? '#b91c1c' : active ? '#166534' : '#92400e',
          }} />
      </Box>
      <Divider sx={{ mb: 3 }} />

      <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 1.5 }}>
        {editing ? (
          <Box sx={{ display: 'flex', gap: 1 }}>
            <Button size="small" variant="outlined" onClick={cancelEdit}
              sx={{ color: '#64748b', borderColor: '#cbd5e1', textTransform: 'none', borderRadius: 1.5 }}>
              Cancel
            </Button>
            <Button size="small" variant="contained" onClick={handleSave} disabled={saving}
              sx={{ bgcolor: '#075e54', '&:hover': { bgcolor: '#0a7a6c' }, textTransform: 'none', borderRadius: 1.5 }}>
              {saving ? 'Saving...' : 'Save Changes'}
            </Button>
          </Box>
        ) : (
          <Button size="small" variant="outlined" startIcon={<EditIcon />} onClick={startEdit}
            sx={{ color: '#075e54', borderColor: '#075e54', textTransform: 'none', borderRadius: 1.5 }}>
            Edit Details
          </Button>
        )}
      </Box>

      {editing && (
        <Typography fontSize={12} color="#94a3b8" mb={2}>
          <LockIcon sx={{ fontSize: 13, verticalAlign: 'middle', mr: 0.5 }} />
          Email,           Company Name, WhatsApp Business Number and Facebook BM ID are locked and cannot be changed here.
        </Typography>
      )}

      {/* Details grid */}
      <Grid container spacing={2.5}>
        {(editing ? editableFields : [...editableFields, ...lockedFields]).map((f) => (
          <Grid item xs={12} sm={6} key={f.label}>
            {editing ? renderInput(f) : renderValue(f)}
          </Grid>
        ))}
        {editing && lockedFields.map((f) => (
          <Grid item xs={12} sm={6} key={f.label}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, p: 1.25, borderRadius: 2, bgcolor: '#f1f5f9', border: '1px dashed #cbd5e1', minHeight: 52, opacity: 0.7 }}>
              <Box sx={{
                width: 32, height: 32, borderRadius: 2, flexShrink: 0,
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                bgcolor: '#e2e8f0', color: '#64748b', fontSize: 17,
              }}>
                {f.icon}
              </Box>
              <Box sx={{ minWidth: 0, flex: 1 }}>
                <Typography fontSize={11} color="#94a3b8" fontWeight={600}>{f.label}</Typography>
                <Typography fontSize={13.5} color="#475569" fontWeight={600} sx={{ wordBreak: 'break-word' }}>
                  {f.value || '—'}
                </Typography>
              </Box>
              <LockIcon sx={{ fontSize: 15, color: '#94a3b8' }} />
            </Box>
          </Grid>
        ))}
      </Grid>

      <Divider sx={{ my: 4 }} />

      {/* Change password */}
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: pwOpen ? 2 : 0 }}>
        <Typography fontWeight={700} fontSize={15} color="#1e293b">Password</Typography>
        <Button size="small" variant="outlined" onClick={() => { setPwOpen(!pwOpen); setPwForm({ oldPassword: '', newPassword: '' }); }}
          sx={{ color: '#075e54', borderColor: '#075e54', textTransform: 'none', borderRadius: 1.5 }}>
          {pwOpen ? 'Cancel' : 'Change Password'}
        </Button>
      </Box>
      {pwOpen && (
        <Box sx={{ bgcolor: '#f8fafc', border: '1px solid #eef2f7', borderRadius: 2, p: 2 }}>
          <TextField size="small" label="Old Password" type="password" fullWidth
            value={pwForm.oldPassword}
            onChange={(e) => setPwForm({ ...pwForm, oldPassword: e.target.value })}
            sx={{ mb: 2, "& .MuiOutlinedInput-root": { bgcolor: '#fff', borderRadius: 1.5 } }} />
          <TextField size="small" label="New Password" type="password" fullWidth
            value={pwForm.newPassword}
            onChange={(e) => setPwForm({ ...pwForm, newPassword: e.target.value })}
            sx={{ mb: 1.5, "& .MuiOutlinedInput-root": { bgcolor: '#fff', borderRadius: 1.5 } }} />
          <Typography fontSize={11.5} color="#94a3b8" mb={1.5}>
            New password must be at least 8 characters with uppercase, lowercase, number and a special character.
          </Typography>
          <Button size="small" variant="contained" onClick={handleChangePassword} disabled={pwSaving}
            sx={{ bgcolor: '#075e54', '&:hover': { bgcolor: '#0a7a6c' }, textTransform: 'none', borderRadius: 1.5 }}>
            {pwSaving ? 'Saving...' : 'Update Password'}
          </Button>
        </Box>
      )}

      <Snackbar open={snack.open} autoHideDuration={3000} onClose={() => setSnack({ ...snack, open: false })}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}>
        <Alert onClose={() => setSnack({ ...snack, open: false })} severity={snack.severity}
          sx={{ width: '100%', borderRadius: 2 }}>{snack.message}</Alert>
      </Snackbar>
    </Box>
  );
};

export default AccountDetails;
